/* eslint-disable prettier/prettier */

import { useCallback, useEffect, useState } from 'react';

import {
	goalService,
	Goal,
	GoalFilters,
	CreateGoalPayload,
	UpdateGoalPayload,
	UpdateGoalProgressPayload,
} from '../services/goal.service';

export interface GoalMutationResult {
	success: boolean;
	message?: string;
}

/**
 * Goal Hook
 * Handles all frontend Goal logic
 */
export const useGoal = (initialFilters?: GoalFilters) => {
	/**
	 * Goal State
	 */
	const [goals, setGoals] = useState<Goal[]>([]);

	const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);

	/**
	 * Pagination State
	 */
	const [total, setTotal] = useState<number>(0);

	const [page, setPage] = useState<number>(1);

	const [limit, setLimit] = useState<number>(20);

	/**
	 * Loading States
	 */
	const [loading, setLoading] = useState<boolean>(false);

	const [creating, setCreating] = useState<boolean>(false);

	const [updating, setUpdating] = useState<boolean>(false);

	const [deleting, setDeleting] = useState<boolean>(false);

	const [assigning, setAssigning] = useState<boolean>(false);

	/**
	 * Error State
	 */
	const [error, setError] = useState<string | null>(null);

	/**
	 * Fetch Goals
	 */
	const fetchGoals = useCallback(
		async (filters?: GoalFilters) => {
			try {
				setLoading(true);

				setError(null);

				const response = await goalService.getGoals({
					...initialFilters,
					...filters,
				});

				console.log('Fetch Goals Response:', response);

				if (response.success && response.data) {
					setGoals(response.data.goals);

					setTotal(response.data.total);

					setPage(response.data.page);

					setLimit(response.data.limit);
				} else {
					setError(response.message || 'Failed to fetch goals');
				}
			} catch (err: any) {
				console.error('Fetch Goals Error:', err);

				setError(
					err.message || 'Something went wrong while fetching goals'
				);
			} finally {
				setLoading(false);
			}
		},
		[initialFilters]
	);

	/**
	 * Fetch Single Goal
	 */
	const fetchGoalById = async (goalId: string) => {
		try {
			setLoading(true);

			setError(null);

			const response = await goalService.getGoalById(goalId);

			if (response.success && response.data) {
				setSelectedGoal(response.data);

				return response.data;
			}

			setError(response.message || 'Failed to fetch goal');

			return null;
		} catch (err: any) {
			console.error('Fetch Goal Error:', err);

			setError(err.message || 'Something went wrong while fetching goal');

			return null;
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Create Goal
	 */
	const createGoal = async (
		payload: CreateGoalPayload
	): Promise<GoalMutationResult> => {
		try {
			setCreating(true);

			setError(null);

			const response = await goalService.createGoal(payload);

			if (response.success && response.data) {
				/**
				 * Instant UI Refresh
				 */
				setGoals(prev => [response.data!, ...prev]);

				return { success: true };
			}

			const message = response.message || 'Failed to create goal';
			setError(message);

			return { success: false, message };
		} catch (err: any) {
			console.error('Create Goal Error:', err);

			// The server's message matters here: a duplicate goal type explains which
			// existing goal holds the type, which a generic string would throw away.
			const message =
				err?.message || 'Something went wrong while creating goal';
			setError(message);

			return { success: false, message };
		} finally {
			setCreating(false);
		}
	};

	/**
	 * Update Goal
	 */
	const updateGoal = async (
		goalId: string,
		payload: UpdateGoalPayload
	): Promise<GoalMutationResult> => {
		try {
			setUpdating(true);

			setError(null);

			const response = await goalService.updateGoal(goalId, payload);

			if (response.success && response.data) {
				/**
				 * Update goal in list
				 */
				setGoals(prev =>
					prev.map(goal =>
						goal.id === goalId ? response.data! : goal
					)
				);

				/**
				 * Update selected goal if opened
				 */
				if (selectedGoal?.id === goalId) {
					setSelectedGoal(response.data);
				}

				return { success: true };
			}

			const message = response.message || 'Failed to update goal';
			setError(message);

			return { success: false, message };
		} catch (err: any) {
			console.error('Update Goal Error:', err);

			const message =
				err?.message || 'Something went wrong while updating goal';
			setError(message);

			return { success: false, message };
		} finally {
			setUpdating(false);
		}
	};

	/**
	 * Assign Template Goal
	 */
	const assignTemplateGoalToProgram = async (
		goalId: string,
		programId: string
	): Promise<boolean> => {
		try {
			setAssigning(true);

			setError(null);

			const response = await goalService.assignTemplateGoalToProgram(
				goalId,
				programId
			);

			if (response.success) {
				return true;
			}

			setError(response.message || 'Failed to assign goal');

			return false;
		} catch (err: any) {
			console.error('Assign Goal Error:', err);

			setError(
				err.message || 'Something went wrong while assigning goal'
			);

			return false;
		} finally {
			setAssigning(false);
		}
	};

	/**
	 * Update Goal Progress
	 */
	const updateGoalProgress = async (
		goalId: string,
		payload: UpdateGoalProgressPayload
	): Promise<boolean> => {
		try {
			setUpdating(true);

			setError(null);

			const response = await goalService.updateGoalProgress(
				goalId,
				payload
			);

			if (response.success && response.data) {
				/**
				 * Instant UI Refresh
				 */
				setGoals(prev =>
					prev.map(goal =>
						goal.id === goalId ? response.data! : goal
					)
				);

				/**
				 * Update selected goal
				 */
				if (selectedGoal?.id === goalId) {
					setSelectedGoal(response.data);
				}

				return true;
			}

			setError(response.message || 'Failed to update goal progress');

			return false;
		} catch (err: any) {
			console.error('Update Goal Progress Error:', err);

			setError(
				err.message ||
					'Something went wrong while updating goal progress'
			);

			return false;
		} finally {
			setUpdating(false);
		}
	};

	/**
	 * Delete Goal
	 */
	const deleteGoal = async (goalId: string): Promise<boolean> => {
		try {
			setDeleting(true);

			setError(null);

			const response = await goalService.deleteGoal(goalId);

			if (response.success) {
				/**
				 * Instant UI Refresh
				 */
				setGoals(prev => prev.filter(goal => goal.id !== goalId));

				return true;
			}

			setError(response.message || 'Failed to delete goal');

			return false;
		} catch (err: any) {
			console.error('Delete Goal Error:', err);

			setError(err.message || 'Something went wrong while deleting goal');

			return false;
		} finally {
			setDeleting(false);
		}
	};

	/**
	 * Initial Fetch
	 */
	useEffect(() => {
		fetchGoals();
	}, [fetchGoals]);

	return {
		/**
		 * Goal States
		 */
		goals,

		selectedGoal,

		total,

		page,

		limit,

		/**
		 * Loading States
		 */
		loading,

		creating,

		updating,

		deleting,

		assigning,

		/**
		 * Error State
		 */
		error,

		/**
		 * Actions
		 */
		fetchGoals,

		fetchGoalById,

		createGoal,

		updateGoal,

		assignTemplateGoalToProgram,

		updateGoalProgress,

		deleteGoal,
	};
};
